/**
 * AJAX Filter Handler
 * Handles collection filtering using Shopify's Section Rendering API
 */

class AjaxFilterHandler {
  constructor() {
    this.form = null;
    this.collectionContainer = null;
    this.isLoading = false;
    this.isSearchPage = false;
    this.init();
  }

  init() {
    this.bindEvents();
    this.findElements();
    this.detectPageType();
    this.bindHistoryEvents();
  }

  bindHistoryEvents() {
    window.addEventListener('popstate', async () => {
      if (this.isLoading) return;

      try {
        this.setLoadingState(true);

        const url = window.location.href;
        const response = await this.fetchUpdate(url);

        if (response.ok) {
          const html = await response.text();
          await this.updateContent(html);
        }
      } catch (err) {
        console.error('History navigation update failed:', err);
      } finally {
        this.setLoadingState(false);
      }
    });
  }

  detectPageType() {
    // Check if we're on a search page
    this.isSearchPage = window.location.pathname.includes('/search') || 
                        document.querySelector('[data-search-page]') !== null;
  }

  findElements() {
    // Find the collection container that needs to be updated
    this.collectionContainer = document.querySelector('ajax-pagination') || 
                              document.querySelector('[data-ajax-content]') ||
                              document.querySelector('.search-results') ||
                              document.querySelector('.plp-wrap') ||
                              document.querySelector('[data-section-id="collection"]') ||
                              document.querySelector('[data-section-id="search"]');
    
    if (!this.collectionContainer) {
      console.warn('Collection container not found for AJAX filtering');
    }
  }

  bindEvents() {
    // Listen for apply filters button clicks
    document.addEventListener('click', (e) => {
      if (e.target.matches('.apply-filters')) {
        e.preventDefault();
        this.form = e.target.closest('form');
        if (this.form) {
          this.handleFilterSubmit();
        }
      }
    });

    // Listen for individual filter changes (checkboxes, sort dropdown, etc.)
    document.addEventListener('change', (e) => {
      if (e.target.matches('.filter-checkbox, .price-input-group input, .sort-select')) {
        this.form = e.target.closest('form');
        
        // Auto-apply filters for sort dropdown changes
        if (e.target.matches('.sort-select')) {
          this.handleFilterSubmit();
        }
        // Optional: Auto-apply filters on other changes (uncomment if desired)
        // else {
        //   this.handleFilterSubmit();
        // }
      }
    });

    // Listen for clear filter links
    document.addEventListener('click', (e) => {
      if (e.target.matches('.clear-filter, .clear-all-filters, .active-filter-tag')) {
        e.preventDefault();
        this.handleClearFilter(e.target);
      }
    });
  }

  async handleFilterSubmit() {
    if (this.isLoading || !this.form) return;

    try {
      this.setLoadingState(true);
      
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData);
      
      // Get current collection URL
      const currentUrl = new URL(window.location);
      
      // Build the new URL with filters
      let newUrl;
      if (this.isSearchPage) {
        // For search pages, maintain the search query
        const searchQuery = formData.get('q') || currentUrl.searchParams.get('q') || '';
        const prefix = formData.get('options[prefix]') || currentUrl.searchParams.get('options[prefix]') || 'last';
        
        // Start with base search params
        const params = new URLSearchParams();
        params.set('q', searchQuery);
        params.set('options[prefix]', prefix);
        
        // Add all other form params (filters, sort_by)
        for (const [key, value] of searchParams.entries()) {
          if (key !== 'q' && !key.startsWith('options[prefix]')) {
            params.append(key, value);
          }
        }
        
        newUrl = `/search?${params.toString()}`;
      } else {
        // For collection pages
        const collectionPath = currentUrl.pathname;
        newUrl = `${collectionPath}?${searchParams.toString()}`;
      }
      
      // Fetch the updated collection/search using Section Rendering API
      const response = await this.fetchUpdate(newUrl);
      
      if (response.ok) {
        const html = await response.text();
        await this.updateContent(html);
        this.updateUrl(newUrl);
        this.closeFilterPanel();
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
    } catch (error) {
      console.error('Filter update failed:', error);
      const errorText = this.form?.dataset.failedUpdateText || 'Failed to update filters. Please try again.';
      this.showError(errorText);
    } finally {
      this.setLoadingState(false);
    }
  }

  async handleClearFilter(element) {
    if (this.isLoading) return;

    try {
      this.setLoadingState(true);
      
      const href = element.getAttribute('href');
      if (!href) return;
      
      const response = await this.fetchUpdate(href);
      
      if (response.ok) {
        const html = await response.text();
        await this.updateContent(html);
        this.updateUrl(href);
        this.closeFilterPanel();
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
    } catch (error) {
      console.error('Clear filter failed:', error);
      const errorText = this.form?.dataset.failedClearText || 'Failed to clear filters. Please try again.';
      this.showError(errorText);
    } finally {
      this.setLoadingState(false);
    }
  }

  async fetchUpdate(url) {
    const ajaxPagination = document.querySelector('ajax-pagination');
    let sectionId;

    if (ajaxPagination && ajaxPagination.dataset.sectionId) {
      sectionId = ajaxPagination.dataset.sectionId;
    } else {
      sectionId = this.isSearchPage ? 'search' : 'collection';
    }

    // Use Section Rendering API to get the full section including filters
    const sectionUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=${sectionId}`;
    
    return fetch(sectionUrl, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/html',
      },
    });
  }

  async updateContent(html) {
    if (!this.collectionContainer) {
      this.findElements();
      if (!this.collectionContainer) {
        throw new Error('Container not found');
      }
    }

    // Create a temporary container to parse the new HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Find the new collection content - look for ajax-pagination first
    const newAjaxPagination = tempDiv.querySelector('ajax-pagination');
    const newProductGrid = tempDiv.querySelector('[data-ajax-content]') || 
                          tempDiv.querySelector('.plp-wrap') ||
                          tempDiv.querySelector('.search-results-sections');
    const newPaginationControls = tempDiv.querySelector('.ajax-pagination-controls');

    if (newAjaxPagination) {
      // Replace the entire ajax-pagination component
      this.collectionContainer.outerHTML = newAjaxPagination.outerHTML;
      // Update reference to the new element
      this.findElements();
    } else if (newProductGrid) {
      // Just update the product grid if ajax-pagination wrapper not found
      const currentProductGrid = this.collectionContainer.querySelector('[data-ajax-content]') || 
                                this.collectionContainer.querySelector('.plp-wrap') ||
                                this.collectionContainer.querySelector('.search-results-sections');
      if (currentProductGrid) {
        currentProductGrid.innerHTML = newProductGrid.innerHTML;
      }
      
      // Update pagination controls if they exist
      if (newPaginationControls) {
        const currentPaginationControls = document.querySelector('.ajax-pagination-controls');
        if (currentPaginationControls) {
          currentPaginationControls.outerHTML = newPaginationControls.outerHTML;
        }
      }
    }
    
    // Update active filters display
    this.updateActiveFilters(tempDiv);
    
    // Update filter counts in the filter panel
    this.updateFilterCounts(tempDiv);
    
    // Update filter form inputs to reflect current state
    this.updateFilterForm(tempDiv);
    
    // Re-initialize any components that might be needed
    this.reinitializeComponents();
    
    // Scroll to top of collection filters with header offset
    const scrollTarget = document.querySelector('.collection-filters') || 
                        document.querySelector('ajax-pagination') || 
                        this.collectionContainer;
    
    if (scrollTarget) {
      this.scrollToElementWithOffset(scrollTarget);
    }
  }

  updateActiveFilters(tempDiv) {
    // Find the new active filters section in the response
    const newActiveFilters = tempDiv.querySelector('.active-filters');
    const currentActiveFilters = document.querySelector('.active-filters');
    
    if (newActiveFilters && currentActiveFilters) {
      // Replace the entire active filters section
      currentActiveFilters.outerHTML = newActiveFilters.outerHTML;
    } else if (!newActiveFilters && currentActiveFilters) {
      // Remove active filters if none exist in the new response
      currentActiveFilters.remove();
    } else if (newActiveFilters && !currentActiveFilters) {
      // Add active filters if they don't exist but are in the new response
      const filtersContainer = document.querySelector('.collection-filters');
      if (filtersContainer) {
        filtersContainer.appendChild(newActiveFilters.cloneNode(true));
      }
    }
  }

  updateFilterCounts(tempDiv) {
    // Update the main FILTERS button count
    const newFiltersToggle = tempDiv.querySelector('.filters-main-toggle');
    const currentFiltersToggle = document.querySelector('.filters-main-toggle');
    
    if (newFiltersToggle && currentFiltersToggle) {
      const newCount = newFiltersToggle.querySelector('.filters-count');
      const currentCount = currentFiltersToggle.querySelector('.filters-count');
      
      if (newCount && currentCount) {
        currentCount.textContent = newCount.textContent;
      } else if (newCount && !currentCount) {
        // Add count if it doesn't exist
        const filtersLabel = currentFiltersToggle.querySelector('.filters-label');
        if (filtersLabel) {
          filtersLabel.insertAdjacentHTML('afterend', newCount.outerHTML);
        }
      } else if (!newCount && currentCount) {
        // Remove count if it no longer exists
        currentCount.remove();
      }
    }

    // Update individual filter section counts in the panel
    const newFilterSections = tempDiv.querySelectorAll('.filter-section-title');
    newFilterSections.forEach(newSection => {
      const filterText = newSection.textContent.split('(')[0].trim();
      const currentSection = Array.from(document.querySelectorAll('.filter-section-title')).find(
        section => section.textContent.split('(')[0].trim() === filterText
      );
      
      if (currentSection) {
        currentSection.textContent = newSection.textContent;
      }
    });
  }

  updateFilterForm(tempDiv) {
    // Update the filter form to reflect the current state
    const newFilterForm = tempDiv.querySelector('.filters-form');
    const currentFilterForm = document.querySelector('.filters-form');
    
    if (newFilterForm && currentFilterForm) {
      // Update checkbox states
      const newCheckboxes = newFilterForm.querySelectorAll('.filter-checkbox, input[type="checkbox"]');
      newCheckboxes.forEach(newCheckbox => {
        const currentCheckbox = currentFilterForm.querySelector(`input[name="${newCheckbox.name}"][value="${newCheckbox.value}"]`);
        if (currentCheckbox) {
          currentCheckbox.checked = newCheckbox.checked;
        }
      });
      
      // Update price range inputs
      const newPriceInputs = newFilterForm.querySelectorAll('input[type="number"]');
      newPriceInputs.forEach(newInput => {
        const currentInput = currentFilterForm.querySelector(`input[name="${newInput.name}"]`);
        if (currentInput) {
          currentInput.value = newInput.value;
        }
      });
      
      // Update price slider values and displays
      const newSliderContainers = newFilterForm.querySelectorAll('.price-slider-container');
      newSliderContainers.forEach((newContainer, index) => {
        const currentContainer = currentFilterForm.querySelectorAll('.price-slider-container')[index];
        if (currentContainer) {
          // Update slider values
          const newMinSlider = newContainer.querySelector('.price-slider-min');
          const newMaxSlider = newContainer.querySelector('.price-slider-max');
          const currentMinSlider = currentContainer.querySelector('.price-slider-min');
          const currentMaxSlider = currentContainer.querySelector('.price-slider-max');
          
          if (newMinSlider && currentMinSlider) {
            currentMinSlider.value = newMinSlider.value;
          }
          if (newMaxSlider && currentMaxSlider) {
            currentMaxSlider.value = newMaxSlider.value;
          }
          
          // Update price displays
          const newMinValue = newContainer.querySelector('.price-slider-min-value');
          const newMaxValue = newContainer.querySelector('.price-slider-max-value');
          const currentMinValue = currentContainer.querySelector('.price-slider-min-value');
          const currentMaxValue = currentContainer.querySelector('.price-slider-max-value');
          
          if (newMinValue && currentMinValue) {
            currentMinValue.textContent = newMinValue.textContent;
          }
          if (newMaxValue && currentMaxValue) {
            currentMaxValue.textContent = newMaxValue.textContent;
          }
          
          // Update slider range visual
          const newRange = newContainer.querySelector('.price-slider-range');
          const currentRange = currentContainer.querySelector('.price-slider-range');
          if (newRange && currentRange) {
            currentRange.style.left = newRange.style.left;
            currentRange.style.width = newRange.style.width;
          }
        }
      });
    }
  }

  reinitializeComponents() {
    // Re-initialize any components that might be in the updated content
    
    // Re-initialize lazy loading images
    if (window.lazyLoadInstance) {
      window.lazyLoadInstance.update();
    }
    
    // Re-initialize price range sliders
    const sliderContainers = document.querySelectorAll('.price-slider-container:not([data-initialized])');
    sliderContainers.forEach(container => {
      if (window.PriceRangeSlider) {
        new window.PriceRangeSlider(container);
        container.dataset.initialized = 'true';
      }
    });
    
    // Re-initialize any custom elements that might be in the new content
    const customElements = this.collectionContainer.querySelectorAll('[is], [data-component]');
    customElements.forEach(element => {
      // Trigger any initialization that might be needed
      if (element.connectedCallback) {
        element.connectedCallback();
      }
    });
    
    // Dispatch a custom event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('collection:updated', {
      detail: { container: this.collectionContainer }
    }));
  }

  scrollToElementWithOffset(element) {
    // Get the header height from CSS custom property
    const headerHeight = getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height')
      .trim();
    
    // Parse the header height value (could be in px, rem, etc.)
    let offsetPixels = 0;
    if (headerHeight) {
      if (headerHeight.includes('px')) {
        offsetPixels = parseInt(headerHeight);
      } else if (headerHeight.includes('rem')) {
        const remValue = parseFloat(headerHeight);
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        offsetPixels = remValue * rootFontSize;
      } else if (headerHeight.includes('em')) {
        const emValue = parseFloat(headerHeight);
        const fontSize = parseFloat(getComputedStyle(element).fontSize);
        offsetPixels = emValue * fontSize;
      } else {
        // Assume it's already in pixels if no unit
        offsetPixels = parseInt(headerHeight) || 0;
      }
    }
    
    // Add some extra padding for better visual spacing
    const extraPadding = 20;
    const totalOffset = offsetPixels + extraPadding;
    
    // Get element position
    const elementRect = element.getBoundingClientRect();
    const elementTop = elementRect.top + window.pageYOffset;
    
    // Calculate scroll position with offset
    const scrollToPosition = elementTop - totalOffset;
    
    // Smooth scroll to the calculated position
    window.scrollTo({
      top: Math.max(0, scrollToPosition), // Ensure we don't scroll above the page
      behavior: 'smooth'
    });
  }

  updateUrl(newUrl) {
    // Update the browser URL without reloading the page
    if (window.history && window.history.pushState) {
      window.history.pushState({ path: newUrl }, '', newUrl);
    }
  }

  closeFilterPanel() {
    // Close the filter fly-out panel
    const activePanel = document.querySelector('.fly-out-panel.active');
    if (activePanel) {
      const closeButton = activePanel.querySelector('.js-fly-out-panel-toggle');
      if (closeButton) {
        closeButton.click();
      }
    }
  }

  setLoadingState(loading) {
    this.isLoading = loading;
    
    // Add loading class to collection container
    if (this.collectionContainer) {
      this.collectionContainer.classList.toggle('loading', loading);
    }
    
    // Disable/enable apply button
    const applyButton = document.querySelector('.apply-filters');
    if (applyButton) {
      applyButton.disabled = loading;
      const form = applyButton.closest('form');
      const applyingText = form?.dataset.applyingText || 'Applying...';
      const applyText = form?.dataset.applyText || 'Apply Filters';
      applyButton.textContent = loading ? applyingText : applyText;
    }
    
    // Show/hide loading indicator
    this.toggleLoadingIndicator(loading);
  }

  getUpdatingText() {
    const form = document.querySelector('.filters-form');
    return form?.dataset.updatingText || 'Updating filters...';
  }

  toggleLoadingIndicator(show) {
    let indicator = document.querySelector('.filter-loading-indicator');
    
    if (show && !indicator) {
      // Create loading indicator
      indicator = document.createElement('div');
      indicator.className = 'filter-loading-indicator';
      indicator.innerHTML = `
        <div class="loading-spinner"></div>
        <span>${this.getUpdatingText()}</span>
      `;
      
      if (this.collectionContainer) {
        this.collectionContainer.style.position = 'relative';
        this.collectionContainer.appendChild(indicator);
      }
    } else if (!show && indicator) {
      indicator.remove();
    }
  }

  showError(message) {
    // Simple error display - you might want to customize this
    const errorDiv = document.createElement('div');
    errorDiv.className = 'filter-error-message';
    errorDiv.textContent = message;
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc3545;
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
    `;
    
    document.body.appendChild(errorDiv);
    
    // Remove after 5 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }
}

// Initialize the AJAX filter handler
document.addEventListener('DOMContentLoaded', () => {
  new AjaxFilterHandler();
});

export default AjaxFilterHandler;
